import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isToday, addMonths, addWeeks, addYears } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { createPayment, getPayments, updatePayment, deletePayment } from '../services/paymentService';
import './Calendar.css';

const Calendar = () => {
  const { currentUser } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [payments, setPayments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    recurrence: 'none',
    category: 'Servicios'
  });

  useEffect(() => {
    loadPayments();
  }, [currentUser]);

  const loadPayments = async () => {
    if (currentUser) {
      const paymentsData = await getPayments(currentUser.uid);
      setPayments(paymentsData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const paymentData = {
      ...formData,
      amount: parseFloat(formData.amount)
    };

    if (editingPayment) {
      await updatePayment(editingPayment.id, paymentData);
    } else {
      await createPayment(currentUser.uid, paymentData);
    }

    setFormData({
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      recurrence: 'none',
      category: 'Servicios'
    });
    setShowForm(false);
    setEditingPayment(null);
    loadPayments();
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      description: payment.description,
      amount: payment.amount.toString(),
      date: payment.date,
      recurrence: payment.recurrence || 'none',
      category: payment.category || 'Servicios'
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar este pago?')) {
      await deletePayment(id);
      loadPayments();
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingPayment(null);
    setFormData({
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      recurrence: 'none',
      category: 'Servicios'
    });
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getPaymentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const directPayments = payments.filter(p => p.date === dateStr);
    const recurringPayments = [];

    // Calcular pagos recurrentes
    payments.forEach(payment => {
      if (payment.recurrence && payment.recurrence !== 'none') {
        const paymentDate = new Date(payment.date);
        let nextDate = new Date(paymentDate);

        // Generar hasta 100 ocurrencias futuras
        for (let i = 0; i < 100; i++) {
          if (format(nextDate, 'yyyy-MM-dd') === dateStr) {
            recurringPayments.push({ ...payment, isRecurring: true });
            break;
          }
          if (nextDate > date) break;

          if (payment.recurrence === 'semanal') {
            nextDate = addWeeks(nextDate, 1);
          } else if (payment.recurrence === 'mensual') {
            nextDate = addMonths(nextDate, 1);
          } else if (payment.recurrence === 'anual') {
            nextDate = addYears(nextDate, 1);
          }
        }
      }
    });

    return [...directPayments, ...recurringPayments];
  };

  const getUpcomingPayments = () => {
    const today = new Date();
    const allPayments = [];

    payments.forEach(payment => {
      const paymentDate = new Date(payment.date);

      if (payment.recurrence === 'none') {
        if (paymentDate >= today) {
          allPayments.push({ ...payment, nextDate: payment.date });
        }
      } else {
        let nextDate = new Date(paymentDate);

        while (nextDate < today) {
          if (payment.recurrence === 'semanal') {
            nextDate = addWeeks(nextDate, 1);
          } else if (payment.recurrence === 'mensual') {
            nextDate = addMonths(nextDate, 1);
          } else if (payment.recurrence === 'anual') {
            nextDate = addYears(nextDate, 1);
          }
        }

        allPayments.push({
          ...payment,
          nextDate: format(nextDate, 'yyyy-MM-dd'),
          isRecurring: true
        });
      }
    });

    return allPayments.sort((a, b) => new Date(a.nextDate) - new Date(b.nextDate)).slice(0, 10);
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const upcomingPayments = getUpcomingPayments();

  return (
    <div className="calendar-page">
      <header>
        <h1>📅 Calendario de Pagos</h1>
        <button onClick={() => setShowForm(!showForm)} className="add-btn">
          {showForm ? 'Cancelar' : '+ Nuevo Pago'}
        </button>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} className="payment-form">
          <input
            type="text"
            placeholder="Descripción del pago"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
          />

          <input
            type="number"
            step="0.01"
            placeholder="Monto"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            required
          />

          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            required
          />

          <select
            value={formData.recurrence}
            onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
          >
            <option value="none">Sin recurrencia</option>
            <option value="semanal">Semanal</option>
            <option value="mensual">Mensual</option>
            <option value="anual">Anual</option>
          </select>

          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          >
            <option value="Servicios">Servicios</option>
            <option value="Vivienda">Vivienda</option>
            <option value="Transporte">Transporte</option>
            <option value="Salud">Salud</option>
            <option value="Educación">Educación</option>
            <option value="Entretenimiento">Entretenimiento</option>
            <option value="Otros">Otros</option>
          </select>

          <div className="form-buttons">
            <button type="submit" className="save-btn">
              {editingPayment ? 'Actualizar' : 'Guardar'}
            </button>
            <button type="button" onClick={handleCancel} className="cancel-btn">
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="calendar-controls">
        <button onClick={previousMonth}>← Anterior</button>
        <h2>{format(currentDate, 'MMMM yyyy', { locale: es })}</h2>
        <button onClick={nextMonth}>Siguiente →</button>
      </div>

      <div className="calendar-grid">
        <div className="day-header">Dom</div>
        <div className="day-header">Lun</div>
        <div className="day-header">Mar</div>
        <div className="day-header">Mié</div>
        <div className="day-header">Jue</div>
        <div className="day-header">Vie</div>
        <div className="day-header">Sáb</div>

        {Array.from({ length: monthStart.getDay() }).map((_, i) => (
          <div key={`empty-${i}`} className="calendar-day empty"></div>
        ))}

        {daysInMonth.map(day => {
          const dayPayments = getPaymentsForDate(day);
          return (
            <div
              key={day.toString()}
              className={`calendar-day ${isToday(day) ? 'today' : ''} ${dayPayments.length > 0 ? 'has-payments' : ''}`}
            >
              <div className="day-number">{format(day, 'd')}</div>
              {dayPayments.map((payment, idx) => (
                <div key={`${payment.id}-${idx}`} className="payment-item">
                  <span className="payment-desc">
                    {payment.description}
                    {payment.isRecurring && ' 🔄'}
                  </span>
                  <span className="payment-amount">${payment.amount}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      <div className="upcoming-payments">
        <h3>Próximos Pagos</h3>
        {upcomingPayments.length === 0 ? (
          <p className="no-payments">No hay pagos programados</p>
        ) : (
          upcomingPayments.map((payment, idx) => (
            <div key={`${payment.id}-${idx}`} className="payment-card">
              <div className="payment-info">
                <span className="payment-date">{payment.nextDate}</span>
                <span className="payment-description">
                  {payment.description}
                  {payment.isRecurring && (
                    <span className="recurrence-badge">
                      {payment.recurrence === 'semanal' && '📅 Semanal'}
                      {payment.recurrence === 'mensual' && '📅 Mensual'}
                      {payment.recurrence === 'anual' && '📅 Anual'}
                    </span>
                  )}
                </span>
                <span className="payment-category">{payment.category}</span>
              </div>
              <div className="payment-actions">
                <span className="payment-amount">${payment.amount}</span>
                <button onClick={() => handleEdit(payment)} className="edit-btn">✏️</button>
                <button onClick={() => handleDelete(payment.id)} className="delete-btn">🗑️</button>
              </div>
            </div>
          ))
        )}
      </div>

      <a href="/dashboard" className="back-link">← Volver al Dashboard</a>
    </div>
  );
};

export default Calendar;
